import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router'
import { useRecentSubsystems } from './hooks'
import classNames from 'classnames'
import { requireAuthentication } from '../helpers/authentication'
import './style.scss'
import { FormattedMessage, injectIntl } from 'react-intl'
import IconFunctionalDiagrams from '../components/Icons/IconFunctionalDiagrams'

import { useLocation, useHistory } from 'react-router-dom'
import {
  Input,
  Button,
  Radio,
  Table,
  Drawer,
  Checkbox,
  Row,
  Col,
  Empty,
  Breadcrumb,
  Space,
  Skeleton,
  Spin,
  Tabs,
  Flex,
  Tooltip,
} from 'antd'
import {
  SearchOutlined,
  FilterOutlined,
  LayoutOutlined,
  LeftOutlined,
  HomeOutlined,
  LoadingOutlined,
} from '@ant-design/icons'

import {
  BlockDiagram,
  useBlockDiagram,
  useSubsystems,
  useSubsystemByCode,
  useComponentPinout,
} from '../BlockDiagram'
import { useGlobalSearch } from '../hooks/GlobalSearch'
import { useDiagram, useDiagramDispatch } from './DiagramContext'
import { ComponentOrLocationImage, LocationImage } from './Image'
import { ConnectorPreview } from '../components/ConnectorPreview/index'
import ConnectorPinout from './ConnectorPinout/BlockDiagramPinout'
import {
  PinSelectionProvider,
  usePinSelection,
} from './ConnectorPinout/PinSelectionContext'
import DiagramDetailView, { PinsDetailDiagramView } from './DiagramDetailView'
import { readPinsFocusParams, deletePinsFocusParams } from './deepLink'
import { RelatedSubsystemsButton } from '../components/RelatedSubsystemsButton'
import { useSearchSubsystems } from '../BlockDiagram/hooks'
import { SubsystemsViewTable, searchList } from '../components/SubsystemTable'
import { useFuseDetails } from '../hooks/PowerDistribution'
import { useSelector, shallowEqual } from 'react-redux'
import { Loadable } from '../components/Loading'
import {
  dispatchEvent,
  addPageAttribute,
  removePageAttribute,
} from '../CloudWatch-init'
import { isWindowBelowMinHeight } from '../helpers/utils'
import { useUserPlant } from '../hooks/User'

const DIAGRAMS_VIEW = 'diagrams_view'
const RECENT_VIEW = 'recent_view'

const DIAGRAMS_RECENT_OPTS = [
  {
    label: (
      <FormattedMessage id="functionalDiagrams.diagramRecentOptsLabel.diagramLabel" />
    ),
    value: DIAGRAMS_VIEW,
  },
  {
    label: (
      <FormattedMessage id="functionalDiagrams.diagramRecentOptsLabel.recentLabel" />
    ),
    value: RECENT_VIEW,
  },
]

const DEVICES_VIEW = 'device'
const CONNECTORS_VIEW = 'connector'

const DEVICES_CONNECTORS_OPTS = [
  {
    label: (
      <FormattedMessage id="functionalDiagrams.deviceConnectorsOptsLabel.deviceLabel" />
    ),
    value: DEVICES_VIEW,
  },
  {
    label: (
      <FormattedMessage id="functionalDiagrams.deviceConnectorsOptsLabel.connectorsLabel" />
    ),
    value: CONNECTORS_VIEW,
  },
]

const devicesTableColumns = [
  {
    title: <FormattedMessage id="functionalDiagrams.deviceTable.name" />,
    dataIndex: 'componentId',
  },
  {
    title: <FormattedMessage id="functionalDiagrams.deviceTable.description" />,
    dataIndex: 'description',
  },
]

const connectorsTableColumns = [
  {
    title: <FormattedMessage id="functionalDiagrams.connectorTable.name" />,
    dataIndex: 'componentId',
  },
  {
    title: (
      <FormattedMessage id="functionalDiagrams.connectorTable.description" />
    ),
    dataIndex: 'description',
  },
]

function DiagramsOrRecentControlsHeader(props) {
  const [searchFieldVisible, setSearchFieldVisible] = useState(false)
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()

  const {
    diagramOrRecentTab,
    setSubsystemOrRecentTab,
    setFiltersVisible,
    numOfSelectedFilters,
    searchString,
    setSearchString,
    intl,
  } = props

  const toggleSearchBar = () => {
    setSearchFieldVisible(!searchFieldVisible)
    setSearchString('')
  }

  return (
    <div>
      <div className="functional-diagrams-breadcrumb">
        <div className="functional-diagrams-breadcrumb-wrapper">
          <Breadcrumbs subsystem={diagram.subsystem?.name} />
        </div>
      </div>
      <Flex
        vertical={false}
        justify="space-between"
        className="functional-diagrams-right-controls-header"
      >
        <Button
          icon={searchFieldVisible ? <LeftOutlined /> : <SearchOutlined />}
          onClick={toggleSearchBar}
        />
        {searchFieldVisible ? (
          <>
            <SearchInput
              value={searchString}
              onChange={(e) => setSearchString(e.target.value)}
              intl={intl}
            />
          </>
        ) : (
          <>
            <Radio.Group
              options={DIAGRAMS_RECENT_OPTS}
              onChange={(e) => setSubsystemOrRecentTab(e.target.value)}
              value={diagramOrRecentTab}
              optionType="button"
              buttonStyle="solid"
            />
            <Flex vertical={false} gap="small">
              {numOfSelectedFilters > 0 && (
                <div className="functional-diagrams-filter-selection-info">
                  {`${numOfSelectedFilters} filters applied`}
                </div>
              )}
              <Button
                icon={<FilterOutlined />}
                onClick={() => setFiltersVisible(true)}
              />
            </Flex>
          </>
        )}
      </Flex>
    </div>
  )
}

export const ComponentDetailDiagramBreadcrumbs = ({
  vin,
  subsystemCode,
  subsystemName,
}) => {
  const history = useHistory()
  const items = [
    {
      href: '',
      onClick: (e) => {
        e.preventDefault()
        history.push(`/functional-diagrams/vin/${vin}`)
      },
      title: (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'text-bottom',
          }}
        >
          <IconFunctionalDiagrams width="1.2em" height="1.2em" />
        </span>
      ),
    },
    {
      href: '',
      onClick: (e) => {
        e.preventDefault()
        history.push(
          `/functional-diagrams/vin/${vin}?subsystem_code=${subsystemCode}`,
        )
      },
      title: subsystemName,
    },
  ]
  return <Breadcrumb items={items} />
}

export const Breadcrumbs = ({
  subsystem = null,
  connector = null,
  fromComponentAlias = null,
  toComponentAlias = null,
}) => {
  // Home -> Diagram (Subsystem) -> Selected Edge (Components A and B)
  const dispatch = useDiagramDispatch()
  const items = [
    {
      href: '#',
      onClick: (e) => {
        e.preventDefault()
        dispatch.handleDiagramReset()
      },
      title: (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            verticalAlign: 'text-bottom',
          }}
        >
          <IconFunctionalDiagrams width="1.2em" height="1.2em" />
        </span>
      ),
    },
  ]

  if (subsystem)
    items.push({
      href: '#',
      onClick: (e) => {
        e.preventDefault()
        dispatch.blockDiagram.reset()
      },
      title: subsystem,
    })

  if (connector)
    items.push({
      title: connector,
    })

  if (fromComponentAlias && toComponentAlias)
    items.push({
      title: (
        <>
          {fromComponentAlias} - {toComponentAlias}
        </>
      ),
    })

  return <Breadcrumb items={items} />
}

const SearchInput = ({ value, onChange, intl }) => {
  return (
    <Input
      autoFocus
      allowClear
      className="functional-diagrams-search"
      placeholder={intl.formatMessage({
        id: 'functionalDiagrams.searchInputLabel',
      })}
      value={value}
      onChange={onChange}
    />
  )
}

function DevicesOrConnectorsHeader(props) {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const {
    searchString,
    setSearchString,
    searchFieldVisible,
    setSearchFieldVisible,
    intl,
  } = props

  const toggleSearchBar = () => {
    setSearchFieldVisible(!searchFieldVisible)
    setSearchString('')
  }

  return (
    <Flex
      vertical={false}
      justify="space-between"
      className="functional-diagrams-right-controls-header"
    >
      <Button
        icon={searchFieldVisible ? <LeftOutlined /> : <SearchOutlined />}
        onClick={toggleSearchBar}
      />
      {searchFieldVisible ? (
        <>
          <SearchInput
            value={searchString}
            onChange={(e) => setSearchString(e.target.value)}
            intl={intl}
          />
        </>
      ) : (
        <>
          <Radio.Group
            options={DEVICES_CONNECTORS_OPTS}
            onChange={(e) => {
              dispatch.blockDiagram.handleTabChange({ tab: e.target.value })
            }}
            value={diagram.blockDiagram.tab}
            optionType="button"
            buttonStyle="solid"
          />
          <div className="space-stub" />
        </>
      )}
    </Flex>
  )
}

const DeviceTable = ({ devices, searchString }) => {
  const dispatch = useDiagramDispatch()
  const diagram = useDiagram()
  return (
    <Table
      rowSelection={{
        type: 'radio',
        onSelect: (record, selected, selectedRows, nativeEvent) => {
          dispatch.blockDiagram.handleDeviceTableSelection({
            componentId: record.componentId,
            ids: [record.componentId],
            alias: record.alias,
          })
        },
        selectedRowKeys: [diagram.blockDiagram.table.data.componentId],
      }}
      size="small"
      dataSource={searchList({
        items: devices?.map((item) => {
          return {
            ...item,
            key: item.componentId,
          }
        }),
        searchString,
        fields: [['componentId'], ['description']],
      })}
      columns={devicesTableColumns}
      rowKey={(record) => record.id}
      data-testid="devices-table"
      pagination={{
        pageSize: isWindowBelowMinHeight() ? 12 : 15,
        hideOnSinglePage: true,
      }}
    />
  )
}

const ConnectorTable = ({ connectors, searchString }) => {
  const dispatch = useDiagramDispatch()
  const diagram = useDiagram()
  return (
    <Table
      rowSelection={{
        type: 'radio',
        onSelect: (record, selected, selectedRows, nativeEvent) => {
          dispatch.blockDiagram.handleConnectorTableSelection({
            componentId: record.componentId,
            ids: record.ids,
            imageName: record.imageName,
            alias: record.alias,
          })
        },
        selectedRowKeys: [diagram.blockDiagram.table.data.componentId],
      }}
      size="small"
      dataSource={searchList({
        items: connectors?.map((item) => {
          return {
            ...item,
            key: item.componentId,
          }
        }),
        searchString,
        fields: [['componentId'], ['description']],
      })}
      columns={connectorsTableColumns}
      data-testid="connectors-table"
      pagination={{
        pageSize: isWindowBelowMinHeight() ? 12 : 15,
        hideOnSinglePage: true,
      }}
    />
  )
}

const ComponentTable = ({ intl }) => {
  const diagram = useDiagram()

  const { isSuccess, isLoading, devices, connectors } = useBlockDiagram({
    vin: diagram.vin,
    subsystemId: diagram.subsystem?.id,
  })
  const [searchString, setSearchString] = useState('')
  const [searchFieldVisible, setSearchFieldVisible] = useState(false)

  return (
    <Skeleton
      loading={isLoading}
      paragraph={{ rows: 10 }}
      className="functional-diagrams-loading-padding"
    >
      <DevicesOrConnectorsHeader
        {...{
          searchFieldVisible,
          setSearchFieldVisible,
          searchString,
          setSearchString,
          intl,
        }}
      />
      <div className="functional-diagram-table-wrapper">
        {diagram.blockDiagram.tab === 'device' && (
          <DeviceTable devices={devices} searchString={searchString} />
        )}
        {diagram.blockDiagram.tab === 'connector' && (
          <ConnectorTable connectors={connectors} searchString={searchString} />
        )}
      </div>
    </Skeleton>
  )
}

function DevicesOrConnectors({ intl }) {
  const diagram = useDiagram()
  const { selection, data } = diagram.blockDiagram.graph
  const selectable =
    selection === 'connector' && data.category !== 'power_distribution'

  // Pins the selection was made with (a pinout deep link): open straight on the
  // pins detail diagram, as if the user had checked them and pressed Details.
  // Re-seeds whenever the connector or those pins change — no remount, so the
  // devices/connectors table keeps its search and paging.
  const focusedPins = data.focusedPins || []

  return (
    <PinSelectionProvider
      selectable={selectable}
      resetKey={`${data.componentId ?? ''}:${focusedPins.join(',')}`}
      seedPins={focusedPins}
      seedShowDetail={focusedPins.length > 0}
    >
      <DevicesOrConnectorsInner intl={intl} />
    </PinSelectionProvider>
  )
}

function DevicesOrConnectorsInner({ intl }) {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const { showDetail, reset, selectedPins } = usePinSelection()

  const componentId = diagram.blockDiagram.graph.data.componentId

  const back = () => {
    // Drop the pins the selection was made with too, so a later remount does not
    // re-seed the detail view we are leaving.
    dispatch.blockDiagram.clearFocusedPins()
    reset()
  }

  if (showDetail) {
    // Disable pin selection for any pinout rendered inside the detail view,
    // so it doesn't offer a nested "Details".
    return (
      <PinSelectionProvider selectable={false}>
        <PinsDetailDiagramView
          subsystemCode={diagram.subsystem?.code}
          component={componentId}
          alias={diagram.blockDiagram.graph.data.alias}
          pins={selectedPins}
          onBack={back}
        />
      </PinSelectionProvider>
    )
  }

  return (
    <>
      <div className="functional-diagrams-breadcrumb">
        <div className="functional-diagrams-breadcrumb-wrapper">
          <Breadcrumbs
            subsystem={diagram.subsystem?.name}
            connector={diagram.blockDiagram.graph.data.alias}
          />
        </div>
      </div>
      {diagram.blockDiagram.tab && <ComponentTable intl={intl} />}
      {!diagram.blockDiagram.tab &&
        diagram.blockDiagram.graph.selection === 'device' && <ConnectorView />}
      {!diagram.blockDiagram.tab &&
        diagram.blockDiagram.graph.selection === 'connector' && (
          <ResolvedConnectorView />
        )}
    </>
  )
}

// A connector selected from outside the graph — a table row or a pinout deep link
// — carries only its id until the block diagram resolves the rest. Rendering
// ConnectorView before then would fetch a pinout with no neighbourIds, which the
// backend widens to every neighbour in the vehicle.
function ResolvedConnectorView() {
  const diagram = useDiagram()
  const { resolution } = diagram.blockDiagram.graph.data

  if (resolution === 'pending')
    return (
      <Skeleton
        loading={true}
        paragraph={{ rows: 5 }}
        className="functional-diagrams-loading-padding"
      />
    )

  if (resolution === 'unresolvable') return <NotInDiagram />

  return <ConnectorView />
}

function LeftPanel(props) {
  const { theme, rightPanelVisible } = props
  const dispatch = useDiagramDispatch()
  const diagram = useDiagram()
  const { searchSubsystems } = useSearchSubsystems({
    vin: diagram.vin,
    componentId: diagram.detailDiagram.relatedSubsystemComponentID,
  })
  const [searchString, setSearchString] = useState('')

  return (
    <Flex
      className={classNames('functional-diagrams-left-panel', {
        expand: !rightPanelVisible,
      })}
      vertical={true}
    >
      <div className="functional-diagrams-left-panel-header">
        <div className="functional-diagrams-breadcrumb">
          <Breadcrumb>
            <Breadcrumb.Item>
              {diagram.subsystem ? (
                `${diagram.subsystem?.system.name} — ${diagram.subsystem?.name}`
              ) : (
                <FormattedMessage id="functionalDiagrams.noSelection" />
              )}
            </Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className="functional-diagram-raised-button functional-diagrams-expand-button">
          <Button
            icon={<LayoutOutlined />}
            onClick={() =>
              dispatch.toggleRightPanel({
                rightPanelVisible: !diagram.rightPanelVisible,
              })
            }
            data-testid="collapse-right-panel-button"
          />
        </div>
      </div>
      <Flex className="functional-diagrams-left-panel-content" vertical={true}>
        <div className="functional-diagrams-left-panel-content-diagram">
          <DiagramImageViewer theme={theme} />
        </div>
        {searchSubsystems && (
          <div className="functional-diagrams-left-panel-content-related-subsystems">
            <SubsystemsTable
              subsystems={searchSubsystems}
              searchString={searchString}
              setSearchString={setSearchString}
              pagination={{ pageSize: 2 }}
            />
          </div>
        )}
      </Flex>
    </Flex>
  )
}

export const NoSelection = injectIntl(({ className, intl }) => {
  return (
    <Flex
      vertical={true}
      justify="center"
      align="middle"
      className={classNames('functional-diagrams-empty', className)}
      data-testid="no-selection"
    >
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({
          id: 'functionalDiagrams.noSelection',
        })}
      />
    </Flex>
  )
})

// The selected connector has no port in this subsystem's rendered diagram, so
// there is nothing to highlight and nothing to show a pinout for.
export const NotInDiagram = injectIntl(({ intl }) => {
  return (
    <Flex
      vertical={true}
      justify="center"
      align="center"
      className="functional-diagrams-empty"
      data-testid="connector-not-in-diagram"
    >
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({
          id: 'functionalDiagrams.connectorNotInDiagram',
        })}
      />
    </Flex>
  )
})

export const NoImage = injectIntl(({ intl }) => {
  return (
    <div className="functional-diagrams-empty" data-testid="no-image">
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={intl.formatMessage({ id: 'imageViewer.noImage' })}
      />
    </div>
  )
})

function DiagramImageViewer({ theme }) {
  const diagram = useDiagram()

  if (!diagram.subsystem) return <NoSelection />

  return (
    <BlockDiagram
      key={`${diagram.vin}-${diagram.subsystem.id}`}
      vin={diagram.vin}
      subsystemId={diagram.subsystem.id}
      theme={theme}
    />
  )
}

function SubsystemsTable({
  subsystems,
  searchString,
  setSearchString,
  systemTypesFilter,
  pagination = {},
}) {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()

  const selectSubsystem = ({ record }) => {
    dispatch.handleDiagramReset()
    dispatch.handleSubsystemSelection({ subsystem: record })
    setSearchString('')
  }

  return (
    <SubsystemsViewTable
      subsystems={subsystems}
      searchString={searchString}
      systemTypesFilter={systemTypesFilter}
      pagination={pagination}
      susbsystemCallback={selectSubsystem}
    />
  )
}

function SubsystemsOrRecentTable({
  subsystems,
  selectedTab,
  searchString,
  setSearchString,
  systemTypesFilter,
}) {
  const { recentSubsystemIds } = useRecentSubsystems()

  const recentSubsystems = subsystems.filter((subsystem) =>
    recentSubsystemIds.includes(subsystem.id),
  )

  return (
    <SubsystemsTable
      subsystems={selectedTab === DIAGRAMS_VIEW ? subsystems : recentSubsystems}
      searchString={searchString}
      setSearchString={setSearchString}
      systemTypesFilter={systemTypesFilter}
      pagination={false}
    />
  )
}

function RightPanel(props) {
  const { isLoading, subsystems, visible, intl } = props
  const [searchString, setSearchString] = useState('')
  const [diagramOrRecentTab, setSubsystemOrRecentTab] = useState(DIAGRAMS_VIEW)
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [systemTypesFilter, setSystemTypesFilter] = useState([])
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const { search } = useLocation()
  const history = useHistory()
  const searchParams = new URLSearchParams(search)
  const subsystemId = parseInt(searchParams.get('subsystem'))
  const timeOnSubsystem = useRef(null)
  const [userPlant] = useUserPlant()

  useEffect(() => {
    if (!isLoading && subsystems && subsystemId) {
      const filteredSubsystems = searchSubsystem(subsystemId)
      dispatch.handleSubsystemSelection({ subsystem: filteredSubsystems[0] })

      // Arriving from a pinout with pins picked: select that connector with those
      // pins focused. Only the identity is known here — the block diagram fills
      // in the rest once its graph has loaded.
      const focus = readPinsFocusParams(searchParams)
      if (focus) {
        dispatch.blockDiagram.handleConnectorSelection({
          componentId: focus.componentId,
          focusedPins: focus.pins,
        })
      }

      searchParams.delete('subsystem')
      deletePinsFocusParams(searchParams)
      history.replace({
        search: searchParams.toString(),
      })
    }
  }, [isLoading])

  useEffect(() => {
    window.addEventListener('beforeunload', dispatchSubsystemEvent)
    return () => {
      window.removeEventListener('beforeunload', dispatchSubsystemEvent)
    }
  }, [])

  useEffect(() => {
    if (diagram.subsystem) timeOnSubsystem.current = Date.now()
    return () => {
      dispatchSubsystemEvent()
    }
  }, [diagram.subsystem])

  const dispatchSubsystemEvent = () => {
    if (timeOnSubsystem.current && diagram.subsystem) {
      console.log('sending the following usage event:', {
        subsystem: diagram.subsystem.name,
        timeOnSubsystem: Date.now() - timeOnSubsystem.current,
        plant: userPlant,
      })
      dispatchEvent('FUNCTIONAL_DIAGRAM_SUBSYSTEM_UPDATED', {
        subsystem: diagram.subsystem.name,
        timeOnSubsystem: Date.now() - timeOnSubsystem.current,
        plant: userPlant,
      })
    }
  }

  const searchSubsystem = (filterId) => {
    return subsystems.filter((subsystem) => subsystem.id === filterId)
  }

  return (
    <Flex
      className={classNames('functional-diagrams-right-panel', {
        collapse: !visible,
      })}
      vertical={true}
    >
      <Skeleton
        loading={isLoading}
        paragraph={{ rows: 5 }}
        className="functional-diagrams-loading-padding"
      >
        {diagram.subsystem ? (
          diagram.blockDiagram.graph.selection === 'connection' ? (
            <DiagramDetailView />
          ) : (
            <DevicesOrConnectors intl={intl} />
          )
        ) : (
          <>
            <DiagramsOrRecentControlsHeader
              searchString={searchString}
              setSearchString={setSearchString}
              diagramOrRecentTab={diagramOrRecentTab}
              setSubsystemOrRecentTab={setSubsystemOrRecentTab}
              setFiltersVisible={setFiltersVisible}
              numOfSelectedFilters={systemTypesFilter.length}
              intl={intl}
            />
            <div className="functional-diagram-table-wrapper">
              <SubsystemsOrRecentTable
                selectedTab={diagramOrRecentTab}
                searchString={searchString}
                setSearchString={setSearchString}
                subsystems={subsystems}
                systemTypesFilter={systemTypesFilter}
              />
            </div>
            <Drawer
              title={intl.formatMessage({
                id: 'functionalDiagrams.rightPanel.filter',
              })}
              placement="right"
              onClose={() => setFiltersVisible(false)}
              open={filtersVisible}
              getContainer={false}
              width={260}
              style={{ position: 'absolute' }}
              extra={
                <Space>
                  <Button type="link" onClick={() => setSystemTypesFilter([])}>
                    <FormattedMessage id="functionalDiagrams.rightPanel.clearAll" />
                  </Button>
                </Space>
              }
            >
              <Checkbox.Group
                style={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
                value={systemTypesFilter}
                onChange={(values) => setSystemTypesFilter(values)}
              >
                {Array.from(new Set(subsystems?.map((obj) => obj.system.name)))
                  .sort()
                  .map((obj) => (
                    <Row key={obj}>
                      <Col span={24}>
                        <Checkbox value={obj}>{obj}</Checkbox>
                      </Col>
                    </Row>
                  ))}
              </Checkbox.Group>
            </Drawer>
          </>
        )}
      </Skeleton>
    </Flex>
  )
}

function FunctionalDiagrams({ intl }) {
  const { search } = useLocation()
  const dispatch = useDiagramDispatch()
  const diagram = useDiagram()
  const { vin } = useParams()
  const timeOnFunctionalDiagrams = useRef(null)
  const [userPlant] = useUserPlant()
  const searchParams = new URLSearchParams(search)
  const subsystemCode = searchParams.get('subsystem_code')
  const { subsystem: subsystemByCode } = useSubsystemByCode({
    vin,
    subsystemCode,
  })

  const { isLoading, subsystems } = useSubsystems({ vin })

  useEffect(() => {
    addPageAttribute({ vin })
    timeOnFunctionalDiagrams.current = Date.now()
    window.addEventListener('beforeunload', dispatchFunctionalDiagramUnload)
    return () => {
      removePageAttribute(vin)
      window.removeEventListener(
        'beforeunload',
        dispatchFunctionalDiagramUnload,
      )
    }
  }, [])

  const dispatchFunctionalDiagramUnload = () => {
    if (timeOnFunctionalDiagrams.current) {
      console.log('sending the following usage event:', {
        timeOnFunctionalDiagrams: Date.now() - timeOnFunctionalDiagrams.current,
        plant: userPlant,
      })
      dispatchEvent('FUNCTIONAL_DIAGRAM_UNLOADED', {
        timeOnFunctionalDiagrams: Date.now() - timeOnFunctionalDiagrams.current,
        plant: userPlant,
      })
    }
  }

  const props = useSelector((state) => {
    return {
      theme: state.app.theme,
    }
  }, shallowEqual)

  React.useEffect(() => {
    dispatch.handleVinAdded({ vin })
  }, [vin])

  React.useEffect(() => {
    if (subsystemByCode?.id) {
      dispatch.handleSubsystemSelection({ subsystem: subsystemByCode })
    }
  }, [subsystemByCode?.id])

  return (
    <Flex className="functional-diagrams-root-container">
      <LeftPanel
        rightPanelVisible={diagram.rightPanelVisible}
        theme={props.theme}
      />
      <RightPanel
        isLoading={isLoading}
        subsystems={subsystems}
        visible={diagram.rightPanelVisible}
        intl={intl}
      />
    </Flex>
  )
}

function FuseImageDetails({ fuseDetails }) {
  return (
    <>
      {fuseDetails == null && <></>}
      {fuseDetails != null && (
        <div className="fuse-details-container">
          <div
            className="fuse-color-image-container"
            data-testid="fuse-color-image-container"
          >
            <ConnectorPreview
              title={fuseDetails.title}
              colorCode={fuseDetails.colorCode}
              fileName={fuseDetails.componentImage}
            />
          </div>
          <div
            className="location-details-container"
            data-testid="location-image-container"
          >
            <LocationImage
              fileName={fuseDetails.locationImage}
              componentId={fuseDetails.componentId}
            />
          </div>
        </div>
      )}
    </>
  )
}

function ConnectorView() {
  const diagram = useDiagram()
  const dispatch = useDiagramDispatch()
  const [searchString, updateSearchString] = useState(null)
  const [selectedFuseDetail, updateSelectedFuseDetail] = useState(null)
  const timeOnComponent = useRef(null)
  const [userPlant] = useUserPlant()

  const { results: fuseList, isLoading: fuseListLoading } = useGlobalSearch({
    vin: diagram.vin,
    searchString: searchString,
  })

  const { results: fuseDetails, isLoading: fuseDetailsLoading } =
    useFuseDetails({
      makeModelId:
        !fuseListLoading && fuseList
          ? fuseList[0]?.make_model?.make_model_id
          : null,
      fuseName: !fuseListLoading && fuseList ? fuseList[0]?.alias : null,
    })

  const { isLoading, isSuccess, pinout } = useComponentPinout({
    vin: diagram.vin,
    componentId: diagram.blockDiagram.graph.data.componentId,
    neighborIds: diagram.blockDiagram.graph.data.neighborIds,
  })
  const [activeKeyState, udpateActiveKey] = useState('1')

  useEffect(() => {
    window.addEventListener('beforeunload', dispatchComponentUnload)
    return () => {
      window.removeEventListener('beforeunload', dispatchComponentUnload)
    }
  }, [])

  useEffect(() => {
    timeOnComponent.current = Date.now()
    return () => {
      dispatchComponentUnload()
    }
  }, [diagram.blockDiagram.graph.data.componentId])

  const dispatchComponentUnload = () => {
    if (timeOnComponent.current) {
      console.log('sending the following usage event:', {
        componentId: diagram.blockDiagram.graph.data.componentId,
        timeOnComponent: Date.now() - timeOnComponent.current,
        plant: userPlant,
      })
      dispatchEvent('FUNCTIONAL_DIAGRAM_COMPONENT_UPDATED', {
        componentId: diagram.blockDiagram.graph.data.componentId,
        timeOnComponent: Date.now() - timeOnComponent.current,
        plant: userPlant,
      })
    }
  }

  const items = [
    {
      key: '1',
      label: 'ConnectorPinout',
      children: (
        <div className="functional-diagrams-connector-pinout">
          <ConnectorPinout
            key={diagram.blockDiagram.graph.data.componentId}
            pageSize={isWindowBelowMinHeight() ? 3 : 5}
          />
        </div>
      ),
    },
    {
      key: '2',
      label: (
        <>
          Fuse Details{' '}
          {fuseListLoading && (
            <Spin indicator={<LoadingOutlined spin />} size="small" />
          )}
        </>
      ),
      disabled: selectedFuseDetail == null,
      children: <FuseImageDetails fuseDetails={selectedFuseDetail} />,
    },
  ]

  const isComponentImagePDBox =
    diagram.blockDiagram.graph.data.category == 'power_distribution'
      ? true
      : false

  const onFuseClick = (event) => {
    if (event) {
      const fuseParentGroup = event.target.parentElement.parentElement
      const pattern = /^fuse_.*/
      let fuseIDArray = []
      fuseParentGroup.childNodes.forEach((node) => {
        if (node.id && pattern.test(node.id)) {
          fuseIDArray.push(node.id.substring(5))
        }
      })
      updateSearchString(fuseIDArray.toString())
    } else {
      resetFuseImages()
    }
  }

  useEffect(() => {
    if (!fuseListLoading && !fuseDetailsLoading && fuseList && fuseDetails) {
      udpateActiveKey('2')
      updateSelectedFuseDetail({
        title: `${fuseList[0].alias} - ${fuseDetails.current_ampere}A`,
        colorCode: fuseList[0].color,
        componentImage: fuseList[0].imageName,
        locationImage: fuseList[0].locationImageName,
        componentId: fuseList[0].componentId,
      })
    }
  }, [fuseList, fuseDetails])

  const resetFuseImages = () => {
    updateSearchString(null)
    updateSelectedFuseDetail(null)
    udpateActiveKey('1')
  }

  const onImageSwitch = () => {
    resetFuseImages()
  }

  return (
    <div className="functional-diagrams-right-panel-content">
      <div className="functional-diagrams-block-diagram-image">
        <Loadable showText={false} isLoading={isLoading || !isSuccess}>
          <ComponentOrLocationImage
            componentImageName={diagram.blockDiagram.graph.data.imageName}
            pins={pinout == undefined ? [] : pinout.map((pin) => pin.pin)}
            componentId={diagram.blockDiagram.graph.data.componentId}
            locationImageName={
              diagram.blockDiagram.graph.data.locationImageName
            }
            alias={diagram.blockDiagram.graph.data.alias}
            description={diagram.blockDiagram.graph.data.description}
            isComponentImagePDBox={isComponentImagePDBox}
            onFuseClick={onFuseClick}
            onImageSwitch={onImageSwitch}
            color={diagram.blockDiagram.graph.data.color}
            showSideIcon={diagram.blockDiagram.graph.selection === 'connector'}
            partNumber={diagram.blockDiagram.graph.data.partNumber}
          >
            {diagram.blockDiagram.graph.data.category !== 'inline' && (
              <RelatedSubsystemsButton
                showButtonText={true}
                vin={diagram.vin}
                componentId={diagram.blockDiagram.graph.data.componentId}
                contextComponentId={
                  diagram.detailDiagram.relatedSubsystemComponentID
                }
                componentIdDispatch={dispatch.handleRelatedSubsystemsButton}
              />
            )}
          </ComponentOrLocationImage>
        </Loadable>
      </div>
      <Flex
        className={classNames('functional-diagrams-info-panel')}
        vertical={true}
      >
        {!isComponentImagePDBox && (
          <div className="functional-diagrams-connector-pinout">
            <ConnectorPinout
              key={diagram.blockDiagram.graph.data.componentId}
              pageSize={isWindowBelowMinHeight() ? 4 : 6}
            />
          </div>
        )}
        {isComponentImagePDBox && (
          <Tabs
            activeKey={activeKeyState}
            type="card"
            size="small"
            items={items}
            style={{ flex: 1 }}
            onTabClick={(key, event) => udpateActiveKey(key)}
            data-testid="component-fusedetails-tab"
            className="functional-diagrams-tabs"
          />
        )}
      </Flex>
    </div>
  )
}

export default requireAuthentication(injectIntl(FunctionalDiagrams))
export {
  RelatedDiagramsModal,
  RelatedDiagramsModalButton,
} from './RelatedDiagramsModal'
